import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/errors";
import { toast } from "@/shared/lib/toast";
import { Button } from "@/shared/ui/button";

import { useResolveMapping } from "../api/use-resolve-mapping";
import { useRetryIngest } from "../api/use-retry-ingest";
import type { ReviewRow } from "../lib/review-vm";

interface RowState {
  sino: string;
  error: string;
}

interface ReviewQueueTableProps {
  rows: ReviewRow[];
  canResolve?: boolean;
}

function getApiErrorCode(err: unknown): string | null {
  if (err instanceof ApiError) {
    const body = err.body;
    if (body && typeof body === "object") {
      const b = body as { error?: unknown };
      if (typeof b.error === "string") return b.error;
    }
  }
  return null;
}

export function ReviewQueueTable({ rows, canResolve = true }: ReviewQueueTableProps): JSX.Element {
  const { t } = useTranslation("manual_review");

  const [rowStates, setRowStates] = useState<Record<string, RowState>>(() =>
    Object.fromEntries(rows.map((r) => [r.orderId, { sino: "", error: "" }])),
  );

  const resolveMapping = useResolveMapping();
  const retryIngest = useRetryIngest();

  function getRowState(orderId: string): RowState {
    return rowStates[orderId] ?? { sino: "", error: "" };
  }

  function setSino(orderId: string, value: string): void {
    setRowStates((prev) => ({
      ...prev,
      [orderId]: { sino: value, error: "" },
    }));
  }

  function setError(orderId: string, error: string): void {
    setRowStates((prev) => ({
      ...prev,
      [orderId]: { ...(prev[orderId] ?? { sino: "", error: "" }), error },
    }));
  }

  async function handleResolve(row: ReviewRow): Promise<void> {
    const state = getRowState(row.orderId);
    const sinoSku = state.sino.trim();

    if (!sinoSku) {
      setError(row.orderId, t("err_sino_required"));
      return;
    }

    // Clear any prior error
    setError(row.orderId, "");

    // Step 1: PUT mapping
    try {
      await resolveMapping.mutateAsync({ merchantSku: row.merchantSku, sinoSku });
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === "skumap_unknown_sino") {
        setError(row.orderId, t("err_unknown_sino"));
      } else {
        const msg =
          err instanceof ApiError
            ? `Request failed (${err.status})`
            : err instanceof Error
              ? err.message
              : t("err_unknown_sino");
        setError(row.orderId, msg);
      }
      return; // STOP — do not call retry-ingest
    }

    // Step 2: POST retry-ingest
    try {
      await retryIngest.mutateAsync(row.orderId);
      toast.success(t("toast_retried", { id: row.orderId }));
      // Clear input on success
      setRowStates((prev) => ({
        ...prev,
        [row.orderId]: { sino: "", error: "" },
      }));
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === "order_sku_unmapped") {
        toast.info(t("toast_next_sku"));
      } else if (err instanceof ApiError && err.status === 409) {
        toast.error(t("toast_not_retryable"));
      } else {
        toast.error(t("toast_not_retryable"));
      }
    }
  }

  function isPending(): boolean {
    return resolveMapping.isPending || retryIngest.isPending;
  }

  return (
    <div className="overflow-x-auto rounded-[14px] border border-[var(--border-subtle)] shadow-[var(--sh-1)]">
      <table className="min-w-full border-collapse bg-[var(--surface-raised)]">
        <thead className="border-b border-[var(--border-subtle)] bg-[var(--surface-raised)]">
          <tr>
            <th className="h-11 select-none whitespace-nowrap px-[var(--sp-4)] text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
              {t("col_order")}
            </th>
            <th className="h-11 select-none whitespace-nowrap px-[var(--sp-4)] text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
              {t("col_channel")}
            </th>
            <th className="h-11 select-none whitespace-nowrap px-[var(--sp-4)] text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
              {t("col_created")}
            </th>
            <th className="h-11 select-none whitespace-nowrap px-[var(--sp-4)] text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
              {t("col_sku")}
            </th>
            <th className="h-11 select-none whitespace-nowrap px-[var(--sp-4)] text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
              {t("col_resolve")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border-subtle)]">
          {rows.map((row) => {
            const state = getRowState(row.orderId);
            const pending = isPending();
            return (
              <tr
                key={row.orderId}
                data-testid="review-row"
                className="transition hover:bg-[var(--surface-spotlight)]"
              >
                <td className="whitespace-nowrap px-[var(--sp-4)] py-3 text-sm font-semibold text-[var(--text-primary)]">
                  {row.orderId}
                </td>
                <td className="whitespace-nowrap px-[var(--sp-4)] py-3 text-sm text-[var(--text-secondary)]">
                  <span className="block capitalize">{row.marketplace}</span>
                  <span className="block text-xs text-[var(--text-subtle)]">{row.sellerName}</span>
                </td>
                <td className="whitespace-nowrap px-[var(--sp-4)] py-3 text-sm text-[var(--text-secondary)]">
                  {new Date(row.createdAt).toLocaleDateString()}
                </td>
                <td className="whitespace-nowrap px-[var(--sp-4)] py-3 text-sm">
                  <code className="rounded bg-[var(--surface-sunken)] px-1.5 py-0.5 font-mono text-[12px] text-[var(--text-primary)]">
                    {row.merchantSku}
                  </code>
                </td>
                <td className="px-[var(--sp-4)] py-3">
                  {canResolve ? (
                    <div className="flex items-start gap-2">
                      <div className="flex flex-col gap-1">
                        <input
                          type="text"
                          value={state.sino}
                          onChange={(e) => setSino(row.orderId, e.target.value)}
                          placeholder={t("ph_sino")}
                          disabled={pending}
                          data-testid={`sino-input-${row.orderId}`}
                          className="h-8 w-40 rounded-[var(--r)] border border-[var(--border-default)] bg-[var(--surface-raised)] px-2 text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--brand-500)] focus-ring disabled:opacity-50"
                        />
                        {state.error && (
                          <span
                            className="text-[11px] text-[var(--intent-danger-fg)]"
                            role="alert"
                            data-testid={`sino-error-${row.orderId}`}
                          >
                            {state.error}
                          </span>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pending}
                        onClick={() => void handleResolve(row)}
                        data-testid={`resolve-btn-${row.orderId}`}
                      >
                        {t("btn_resolve_retry")}
                      </Button>
                    </div>
                  ) : (
                    <span className="text-sm text-[var(--text-subtle)]">{t("readonly_hint")}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
